/*
There's a bit more to this component than there now needs to be
 -- it looked like we needed this in more than one place initially.
*/
import type { ChangeEvent, FocusEvent } from 'react';
import React, { useState } from 'react';
import { CloseButton, InputField } from '@noths/polaris-client-ribbons-design-system';
import * as R from 'ramda';

import { MUST_NOT_BE_EMPTY_ERROR } from 'src/components/organisms/Signup/constants/copy';
import * as styles from 'src/components/organisms/Signup/styles/keyValueField.styles';

export interface OtherStore {
  storeName: string;
  weblink: string;
}

export interface OtherOnlineStoreProps {
  // `css` prop on the consumer-side is converted to className
  className?: string;
  name: string;
  onAdd: (name: string, weblink: string) => void;
  onCancel?: () => void;
  storeNamePlaceholder?: string;
  validate: (store: OtherStore) => null | Partial<OtherStoreFieldErrors>;
  valuePlaceholder?: string;
}

export type OtherStoreFieldErrors = Record<keyof OtherStore, null | string>;

export const STORE_NAME_FIELD_LABEL_SUFFIX = 'store-name-field-label';
export const STORE_VALUE_FIELD_LABEL_SUFFIX = 'store-weblink-field-label';

const isEmpty = R.compose(R.isEmpty, R.trim);
const hasEmptyError = R.equals<string | null>(MUST_NOT_BE_EMPTY_ERROR);

export const ARIA_CLOSE_BUTTON_LABEL = 'Close dialog';

export const AddOtherOnlineStore = ({
  className,
  name,
  onAdd,
  onCancel,
  storeNamePlaceholder,
  validate,
  valuePlaceholder,
}: OtherOnlineStoreProps) => {
  const [fieldErrors, setFieldErrors] = useState<OtherStoreFieldErrors>({
    storeName: null,
    weblink: null,
  });

  const [store, setStore] = useState<OtherStore>({ storeName: '', weblink: '' });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const { name, value } = e.target;

    if (hasEmptyError(fieldErrors[name as keyof OtherStore])) {
      setFieldErrors((existingErrors) => ({ ...existingErrors, [name]: null }));
    }

    setStore((existingStore) => ({ ...existingStore, [name]: value }));
  };

  const handleCancelClick = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleAddActionClick = (e: FocusEvent) => {
    e.preventDefault();

    if (e.relatedTarget?.ariaLabel === ARIA_CLOSE_BUTTON_LABEL) {
      return;
    }

    const { storeName, weblink } = store;
    const storeNameError = isEmpty(storeName) ? MUST_NOT_BE_EMPTY_ERROR : null;
    const storeValueError = isEmpty(weblink) ? MUST_NOT_BE_EMPTY_ERROR : null;

    if (storeNameError || storeValueError) {
      setFieldErrors({ storeName: storeNameError, weblink: storeValueError });

      return;
    }

    const validationResult = validate(store);

    if (validationResult != null) {
      setFieldErrors({
        storeName: validationResult.storeName || fieldErrors.storeName,
        weblink: validationResult.weblink || fieldErrors.weblink,
      });

      return;
    }

    onAdd(storeName, weblink);
    setStore(() => ({ storeName: '', weblink: '' }));
  };

  return (
    <div className={className} onBlur={handleAddActionClick}>
      <InputField
        css={styles.hideLabel}
        data-testid="store-name-testid"
        error={fieldErrors.storeName}
        id={`${name}-store-name-field`}
        label={`${name}-${STORE_NAME_FIELD_LABEL_SUFFIX}`}
        name="storeName"
        onChange={handleChange}
        placeholder={storeNamePlaceholder}
        value={store.storeName}
      />
      <InputField
        css={styles.hideLabel}
        data-testid="key-weblink-value"
        error={fieldErrors.weblink}
        id={`${name}-weblink-field`}
        label={`${name}-${STORE_VALUE_FIELD_LABEL_SUFFIX}`}
        name="weblink"
        onChange={handleChange}
        placeholder={valuePlaceholder}
        value={store.weblink}
      />
      {onCancel && (
        <CloseButton
          aria-label={ARIA_CLOSE_BUTTON_LABEL}
          css={styles.offsetCancelButton}
          onClick={handleCancelClick}
          size={'small'}
        />
      )}
    </div>
  );
};
